import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DfService } from '../../../../core/df/df.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { BusinessAiConsentService } from '../../../../core/attestations/business-ai-consent.service';
import { aiConsentBoxNeeded } from '../../../../core/attestations/ai-consent.types';
import { DfFacilityDetail, DfFacilityDocType } from '../../../../core/df/df.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { AiConsentCheckboxComponent } from '../../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DocDropType } from '../../shared/doc-drop/doc-drop';
import { DocUploadPanelComponent, PanelDoc } from '../../shared/doc-upload-panel/doc-upload-panel';
import { readError } from '../../../../core/http/read-error';

type Step = 'details' | 'documents' | 'review';

/** The document buckets DF applicants drop files into — one block each. */
const DOC_TYPES: DocDropType<DfFacilityDocType>[] = [
  { value: 'BANK_STATEMENT', label: 'Bank statements', hint: 'Last 3–6 months' },
  { value: 'FINANCIALS', label: 'Financial statements', hint: 'Annual or management accounts' },
  { value: 'AGE_ANALYSIS', label: 'Debtors age analysis', hint: 'Your current age analysis' },
  { value: 'ID_DOCUMENT', label: 'ID documents', hint: 'Directors’ IDs' },
  { value: 'OTHER', label: 'Other supporting documents', hint: 'Anything else relevant' },
];

/** Suggested facility limits — optional helpers; CapX confirms the final limit. */
const LIMIT_PRESETS = [500_000, 1_000_000, 2_500_000, 5_000_000];

@Component({
  selector: 'app-df-create',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterLink,
    IconComponent,
    ZarPipe,
    DocUploadPanelComponent,
    AiConsentCheckboxComponent,
  ],
  templateUrl: './df-create.html',
  styleUrl: './df-create.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfCreateComponent {
  private readonly dfService = inject(DfService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly DOC_TYPES = DOC_TYPES;
  protected readonly LIMIT_PRESETS = LIMIT_PRESETS;

  protected readonly step = signal<Step>('details');
  protected readonly draft = signal<DfFacilityDetail | null>(null);

  // --- Documents: the shared upload panel owns upload → verdict poll → remove ---

  /** Documents already on the draft — the panel seeds itself from these once. */
  protected readonly initialDocs = computed<PanelDoc<DfFacilityDocType>[]>(
    () => this.draft()?.documents ?? [],
  );
  /** Settled (server-stored) documents, fed by the panel — what actually gets submitted. */
  protected readonly attachedDocs = signal<PanelDoc<DfFacilityDocType>[]>([]);
  /** True while the panel has an upload in flight (blocks "Review & submit"). */
  protected readonly anyInFlight = signal(false);

  protected readonly uploadDoc = (shortId: string, file: File, docType: DfFacilityDocType) =>
    this.dfService.uploadFacilityDocument(shortId, file, docType);
  protected readonly deleteDoc = (shortId: string, docShortId: string) =>
    this.dfService.deleteFacilityDocument(shortId, docShortId);
  protected readonly fetchDocs = (shortId: string) =>
    this.dfService.getFacility(shortId).pipe(map((f) => f.documents ?? []));

  protected readonly isSaving = signal(false);
  protected readonly isSubmitting = signal(false);
  private readonly auth = inject(AuthService);

  /**
   * POPIA s72 — consent to AI-assisted assessment, the BUSINESS's since
   * FLOW-297. Asked only when the business has none standing and this person's
   * sign-up tick cannot be carried to it (`aiConsentBoxNeeded`).
   */
  private readonly businessConsent = inject(BusinessAiConsentService);
  /**
   * The business's consent (FLOW-297, `§A6.7.2`) — read so a second person at a
   * business that has consented is not asked. A failed read falls back to
   * asking; the server decides either way.
   */
  protected readonly businessAiConsent = toSignal(
    this.businessConsent.getState(this.entityId).pipe(
      map((r) => r.ai_consent),
      catchError(() => of(null)),
    ),
    { initialValue: null },
  );
  protected readonly showAiConsentBox = computed(() =>
    aiConsentBoxNeeded(this.businessAiConsent(), this.auth.hasStandingAiConsent()),
  );
  protected readonly aiConsent = signal(false);
  protected readonly aiConsentOk = computed(() => !this.showAiConsentBox() || this.aiConsent());
  protected readonly serverError = signal<string | null>(null);

  protected readonly form = this.fb.group({
    df_intent: this.fb.control<string>('', [Validators.maxLength(2000)]),
    requested_limit: this.fb.control<number | null>(null, [Validators.min(1)]),
  });

  /** Step order — drives the stepper UI and "step N of 3" affordances. */
  protected readonly steps: { id: Step; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'documents', label: 'Documents' },
    { id: 'review', label: 'Review' },
  ];

  protected readonly currentIndex = computed(() =>
    this.steps.findIndex((s) => s.id === this.step()),
  );

  protected stepState(id: Step): 'done' | 'active' | 'upcoming' {
    const i = this.steps.findIndex((s) => s.id === id);
    const current = this.currentIndex();
    if (i < current) return 'done';
    if (i === current) return 'active';
    return 'upcoming';
  }

  protected createDraft(): void {
    if (this.form.invalid || this.isSaving()) {
      this.form.markAllAsTouched();
      return;
    }
    // A draft already exists (user stepped back to Details, then forward) —
    // don't create a second facility, just advance.
    if (this.draft()) {
      this.step.set('documents');
      return;
    }
    this.isSaving.set(true);
    this.serverError.set(null);

    const v = this.form.getRawValue();
    this.dfService
      .createFacility({
        entity: this.entityId,
        df_intent: v.df_intent?.trim() || undefined,
        requested_limit: v.requested_limit != null ? String(v.requested_limit) : undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (draft) => {
          this.draft.set(draft);
          this.step.set('documents');
          this.isSaving.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isSaving.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected pickLimit(amount: number): void {
    this.form.controls.requested_limit.setValue(amount);
    this.form.controls.requested_limit.markAsDirty();
  }

  protected goToStep(id: Step): void {
    // Only allow jumping back to a completed step, never skipping ahead.
    if (this.stepState(id) === 'done') {
      this.step.set(id);
    }
  }

  protected back(): void {
    const prev = this.steps[this.currentIndex() - 1];
    if (prev) {
      this.step.set(prev.id);
    }
  }

  protected toReview(): void {
    this.step.set('review');
  }

  protected submit(): void {
    const draft = this.draft();
    if (!draft || this.isSubmitting() || !this.aiConsentOk()) return;
    this.isSubmitting.set(true);
    this.serverError.set(null);

    const ticked = this.showAiConsentBox() && this.aiConsent();
    this.dfService
      .submitFacility(draft.short_id, this.aiConsentOk())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // The fallback tick also recorded the standing consent server-side.
          if (ticked) this.auth.markAiConsentGiven();
          this.snackBar.open(res.message, 'Dismiss', { duration: 6000 });
          void this.router.navigate(['/entity', this.entityId, 'df', draft.short_id]);
        },
        error: (err: unknown) => {
          this.isSubmitting.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }
}
